import React, { useState, useEffect } from 'react';
import api from '../../../shared/utils/api';

interface AccountPeriodBalanceItemFormData {
  AccountPeriodBalanceOid: string;
  AccountOid: string;
  ActiveAmount: number | null;
  PassiveAmount: number | null;
  BusinessCustomerOid: string;
  // For creating new AccountPeriodBalance
  NewAccountPeriodBalance: {
    CustomerId: string;
    YearType: string;
    ActiveAmount: number | null;
    PassiveAmount: number | null;
  };
  CreateNewAccountPeriodBalance: boolean;
}

interface TableRow {
  id: string;
  Oid?: string; // Existing item Oid for update
  AccountOid: string;
  ActiveAmount: number | null;
  PassiveAmount: number | null;
  BusinessCustomerOid: string;
}

interface AccountPeriodBalanceItemPopupProps {
  isVisible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  accountPeriodBalanceOid?: string;
}

const AccountPeriodBalanceItemPopup: React.FC<AccountPeriodBalanceItemPopupProps> = ({
  isVisible,
  onClose,
  onSuccess,
  accountPeriodBalanceOid,
}) => {
  const [formData, setFormData] = useState<AccountPeriodBalanceItemFormData>({
    AccountPeriodBalanceOid: accountPeriodBalanceOid || '',
    AccountOid: '',
    ActiveAmount: null,
    PassiveAmount: null,
    BusinessCustomerOid: '',
    NewAccountPeriodBalance: {
      CustomerId: '',
      YearType: new Date().getFullYear().toString(),
      ActiveAmount: null,
      PassiveAmount: null,
    },
    CreateNewAccountPeriodBalance: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountPeriodBalances, setAccountPeriodBalances] = useState<Array<{ Oid: string; CustomerName: string; YearType: number }>>([]);
  const [accounts, setAccounts] = useState<Array<{ Oid: string; Code: string; Name: string }>>([]);
  const [customers, setCustomers] = useState<Array<{ Oid: string; CustomerName: string; CustomerID: string }>>([]);
  const [businessCustomers, setBusinessCustomers] = useState<Array<{ Code: string; Name: string }>>([]);
  const [tableRows, setTableRows] = useState<TableRow[]>([
    {
      id: '1',
      AccountOid: '',
      ActiveAmount: null,
      PassiveAmount: null,
      BusinessCustomerOid: '',
    }
  ]);

  useEffect(() => {
    if (accountPeriodBalanceOid) {
      setFormData(prev => ({ ...prev, AccountPeriodBalanceOid: accountPeriodBalanceOid }));
    }
  }, [accountPeriodBalanceOid]);

  useEffect(() => {
    if (isVisible) {
      fetchDropdownData();
      // If accountPeriodBalanceOid is provided, fetch existing items
      if (accountPeriodBalanceOid) {
        fetchExistingItems();
      } else {
        // Reset to default empty row if no accountPeriodBalanceOid
        setTableRows([{
          id: '1',
          AccountOid: '',
          ActiveAmount: null,
          PassiveAmount: null,
          BusinessCustomerOid: '',
        }]);
      }
    }
  }, [isVisible, accountPeriodBalanceOid]);

  const fetchDropdownData = async () => {
    try {
      // Fetch AccountPeriodBalance list
      const balanceResult = await api.get('/api/account-period-balance');
      if (balanceResult?.success && balanceResult?.data) {
        setAccountPeriodBalances(balanceResult.data.map((item: any) => ({
          Oid: item.Oid,
          CustomerName: item.CustomerName || '',
          YearType: item.YearType
        })));
      }

      // Fetch Customers list for current accountant
      await fetchCustomers();

      // Fetch Accounts list
      await fetchAccounts();

      // Fetch BusinessCustomer list
      await fetchBusinessCustomers();
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const handleInputChange = (field: keyof AccountPeriodBalanceItemFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value === '' ? null : value
    }));
    setError('');
    
    // If checkbox is checked, refresh customer list
    if (field === 'CreateNewAccountPeriodBalance' && value === true) {
      fetchCustomers();
    }
  };
  
  const fetchCustomers = async () => {
    try {
      // AccountantOid is automatically extracted from JWT token by backend middleware
      const customersResult = await api.get('/api/customerslist');
      if (customersResult?.success && customersResult?.data) {
        setCustomers(customersResult.data.map((item: any) => ({
          Oid: item.Oid,
          CustomerName: item.CustomerName || '',
          CustomerID: item.CustomerID || ''
        })));
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const accountsResult = await api.get('/api/account?isActive=true');
      if (accountsResult?.success && accountsResult?.data) {
        setAccounts(accountsResult.data.map((item: any) => ({
          Oid: item.Oid,
          Code: item.Code || '',
          Name: item.Name || ''
        })));
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const fetchBusinessCustomers = async () => {
    try {
      const businessCustomersResult = await api.get('/api/business-customer-list');
      if (businessCustomersResult?.success && businessCustomersResult?.data) {
        setBusinessCustomers(businessCustomersResult.data.map((item: any) => ({
          Code: item.Code || '',
          Name: item.Name || ''
        })));
      }
    } catch (error) {
      console.error('Error fetching business customers:', error);
    }
  };

  const fetchExistingItems = async () => {
    try {
      if (!accountPeriodBalanceOid) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ accountPeriodBalanceOid байхгүй байна, fetch хийхгүй');
        }
        return;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📥 AccountPeriodBalanceItem-үүдийг fetch хийж байна:', accountPeriodBalanceOid);
      }
      
      const result = await api.get(`/api/account-period-balance-item?accountPeriodBalanceOid=${accountPeriodBalanceOid}`);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Fetch хийсэн үр дүн:', result);
      }
      
      if (result?.success && result?.data && result.data.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅', result.data.length, 'мөр олдлоо');
        }
        // Map existing items to tableRows format
        const existingRows: TableRow[] = result.data.map((item: any, index: number) => ({
          id: item.Oid || `existing-${index}`,
          Oid: item.Oid, // Store Oid for update
          AccountOid: item.AccountOid || '',
          ActiveAmount: item.ActiveAmount,
          PassiveAmount: item.PassiveAmount,
          BusinessCustomerOid: item.BusinessCustomerOid || '',
        }));
        setTableRows(existingRows);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('ℹ️ Мөр олдсонгүй, хоосон мөр үүсгэж байна');
        }
        // If no items found, keep default empty row
        setTableRows([{
          id: '1',
          AccountOid: '',
          ActiveAmount: null,
          PassiveAmount: null,
          BusinessCustomerOid: '',
        }]);
      }
    } catch (error: any) {
      console.error('🚨 Error fetching existing AccountPeriodBalanceItems:', error);
      if (process.env.NODE_ENV === 'development') {
        console.error('Error details:', error?.response?.data || error?.message);
      }
      // On error, keep default empty row
      setTableRows([{
        id: '1',
        AccountOid: '',
        ActiveAmount: null,
        PassiveAmount: null,
        BusinessCustomerOid: '',
      }]);
    }
  };

  const handleNewAccountPeriodBalanceChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      NewAccountPeriodBalance: {
        ...prev.NewAccountPeriodBalance,
        [field]: value === '' ? null : value
      }
    }));
    setError('');
  };

  const handleAddRow = () => {
    const newRow: TableRow = {
      id: Date.now().toString(),
      AccountOid: '',
      ActiveAmount: null,
      PassiveAmount: null,
      BusinessCustomerOid: '',
    };
    setTableRows([...tableRows, newRow]);
  };

  const handleRemoveRow = (id: string) => {
    if (tableRows.length > 1) {
      setTableRows(tableRows.filter(row => row.id !== id));
    }
  };

  const handleRowChange = (id: string, field: keyof TableRow, value: any) => {
    setTableRows(tableRows.map(row => 
      row.id === id ? { ...row, [field]: value === '' ? null : value } : row
    ));
  };

  // Calculate footer totals
  const calculateFooterTotals = () => {
    const totals = tableRows.reduce((acc, row) => {
      const activeAmount = row.ActiveAmount !== null && row.ActiveAmount !== undefined && !isNaN(row.ActiveAmount)
        ? parseFloat(row.ActiveAmount.toString())
        : 0;
      const passiveAmount = row.PassiveAmount !== null && row.PassiveAmount !== undefined && !isNaN(row.PassiveAmount)
        ? parseFloat(row.PassiveAmount.toString())
        : 0;
      return {
        activeTotal: acc.activeTotal + activeAmount,
        passiveTotal: acc.passiveTotal + passiveAmount,
      };
    }, { activeTotal: 0, passiveTotal: 0 });
    return totals;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let finalAccountPeriodBalanceOid = formData.AccountPeriodBalanceOid;

      // If creating new AccountPeriodBalance
      if (formData.CreateNewAccountPeriodBalance) {
        if (!formData.NewAccountPeriodBalance.CustomerId) {
          setError('Харилцагч заавал шаардлагатай');
          setLoading(false);
          return;
        }

        if (!formData.NewAccountPeriodBalance.YearType) {
          setError('Жилийн төрөл заавал шаардлагатай');
          setLoading(false);
          return;
        }

        // Create new AccountPeriodBalance
        const balanceData = {
          CustomerId: formData.NewAccountPeriodBalance.CustomerId,
          YearType: parseInt(formData.NewAccountPeriodBalance.YearType),
          ActiveAmount: formData.NewAccountPeriodBalance.ActiveAmount,
          PassiveAmount: formData.NewAccountPeriodBalance.PassiveAmount,
        };

        console.log('Creating new AccountPeriodBalance:', balanceData);
        const balanceResult = await api.post('/api/account-period-balance', balanceData);
        
        if (balanceResult?.success && balanceResult?.data) {
          finalAccountPeriodBalanceOid = balanceResult.data.Oid;
          console.log('✅ AccountPeriodBalance шинээр үүсгэлээ:', finalAccountPeriodBalanceOid);
          
          // Refresh AccountPeriodBalance list
          await fetchDropdownData();
        } else {
          throw new Error(balanceResult?.message || 'AccountPeriodBalance үүсгэхэд алдаа гарлаа');
        }
      }

      // Validation for AccountPeriodBalance
      if (!finalAccountPeriodBalanceOid) {
        setError('AccountPeriodBalance заавал шаардлагатай');
        setLoading(false);
        return;
      }


      // Submit all rows - update existing or create new
      const submitPromises = tableRows.map(row => {
        const submitData = {
          AccountPeriodBalanceOid: finalAccountPeriodBalanceOid,
          AccountOid: row.AccountOid || null,
          ActiveAmount: row.ActiveAmount,
          PassiveAmount: row.PassiveAmount,
          BusinessCustomerOid: row.BusinessCustomerOid && row.BusinessCustomerOid.trim() !== '' ? row.BusinessCustomerOid : null,
        };
        
        // If row has Oid, it's an existing item - update it
        if (row.Oid && row.Oid.startsWith('existing-') === false) {
          return api.put(`/api/account-period-balance-item/${row.Oid}`, submitData);
        } else {
          // New item - create it
          return api.post('/api/account-period-balance-item', submitData);
        }
      });

      const results = await Promise.all(submitPromises);
      const failedResults = results.filter(result => !result?.success);
      
      if (failedResults.length > 0) {
        throw new Error(`${failedResults.length} мөр хадгалахад алдаа гарлаа`);
      }

      console.log('✅ Бүх AccountPeriodBalanceItem амжилттай нэмэгдлээ');
      
      // Reset form
      setFormData({
        AccountPeriodBalanceOid: accountPeriodBalanceOid || '',
        AccountOid: '',
        ActiveAmount: null,
        PassiveAmount: null,
        BusinessCustomerOid: '',
        NewAccountPeriodBalance: {
          CustomerId: '',
          YearType: new Date().getFullYear().toString(),
          ActiveAmount: null,
          PassiveAmount: null,
        },
        CreateNewAccountPeriodBalance: false,
      });
      setTableRows([{
        id: '1',
        AccountOid: '',
        ActiveAmount: null,
        PassiveAmount: null,
        BusinessCustomerOid: '',
      }]);
      
      // Call onSuccess to refresh table data
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error submitting AccountPeriodBalanceItem:', error);
      setError(error.response?.data?.message || error.message || 'Мэдээлэл нэмэхэд алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      AccountPeriodBalanceOid: accountPeriodBalanceOid || '',
      AccountOid: '',
      ActiveAmount: null,
      PassiveAmount: null,
      BusinessCustomerOid: '',
      NewAccountPeriodBalance: {
        CustomerId: '',
        YearType: new Date().getFullYear().toString(),
        ActiveAmount: null,
        PassiveAmount: null,
      },
      CreateNewAccountPeriodBalance: false,
    });
    setTableRows([{
      id: '1',
      AccountOid: '',
      ActiveAmount: null,
      PassiveAmount: null,
      BusinessCustomerOid: '',
    }]);
    setError('');
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">AccountPeriodBalanceItem нэмэх</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* AccountPeriodBalance Selection */}
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="createNewBalance"
                checked={formData.CreateNewAccountPeriodBalance}
                onChange={(e) => handleInputChange('CreateNewAccountPeriodBalance', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="createNewBalance" className="ml-2 block text-sm text-gray-700">
                Шинэ AccountPeriodBalance үүсгэх
              </label>
            </div>

            {formData.CreateNewAccountPeriodBalance ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Харилцагч <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.NewAccountPeriodBalance.CustomerId}
                    onChange={(e) => handleNewAccountPeriodBalanceChange('CustomerId', e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  >
                    <option value="">Сонгох...</option>
                    {customers.map((customer) => (
                      <option key={customer.Oid} value={customer.Oid}>
                        {customer.CustomerName} ({customer.CustomerID})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Жилийн төрөл <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.NewAccountPeriodBalance.YearType}
                    onChange={(e) => handleNewAccountPeriodBalanceChange('YearType', e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AccountPeriodBalance <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.AccountPeriodBalanceOid}
                  onChange={(e) => handleInputChange('AccountPeriodBalanceOid', e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                  disabled={!!accountPeriodBalanceOid}
                >
                  <option value="">Сонгох...</option>
                  {accountPeriodBalances.map((balance) => (
                    <option key={balance.Oid} value={balance.Oid}>
                      {balance.CustomerName} - {balance.YearType}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Table Form */}
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <div className="flex justify-between items-center p-2 bg-gray-50 border-b border-gray-300">
              <span className="text-sm font-medium text-gray-700">Мөрүүд</span>
              <button
                type="button"
                onClick={handleAddRow}
                className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-sm flex items-center gap-1"
              >
                ➕ Шинэ мөр нэмэх
              </button>
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 border-b border-gray-300">
                    Данс
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 border-b border-gray-300">
                    Актив
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 border-b border-gray-300">
                    Пассив
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 border-b border-gray-300">
                    Харилцагч
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 border-b border-gray-300">
                    Үйлдэл
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-3 py-2 border-b border-gray-200">
                      <select
                        value={row.AccountOid}
                        onChange={(e) => handleRowChange(row.id, 'AccountOid', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">Сонгох...</option>
                        {accounts.map((account) => (
                          <option key={account.Oid} value={account.Oid}>
                            {account.Code} - {account.Name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200">
                      <input
                        type="number"
                        step="0.01"
                        value={row.ActiveAmount ?? ''}
                        onChange={(e) => handleRowChange(row.id, 'ActiveAmount', e.target.value ? parseFloat(e.target.value) : null)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200">
                      <input
                        type="number"
                        step="0.01"
                        value={row.PassiveAmount ?? ''}
                        onChange={(e) => handleRowChange(row.id, 'PassiveAmount', e.target.value ? parseFloat(e.target.value) : null)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200">
                      <select
                        value={row.BusinessCustomerOid}
                        onChange={(e) => handleRowChange(row.id, 'BusinessCustomerOid', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">Сонгох...</option>
                        <optgroup label="Харилцагч">
                          {customers.map((customer) => (
                            <option key={customer.Oid} value={customer.Oid}>
                              {customer.CustomerName} ({customer.CustomerID})
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Бизнес харилцагч">
                          {businessCustomers.map((businessCustomer, index) => (
                            <option key={`business-${index}`} value={businessCustomer.Code}>
                              {businessCustomer.Code} - {businessCustomer.Name}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </td>
                    <td className="px-3 py-2 border-b border-gray-200">
                      {tableRows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveRow(row.id)}
                          className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm"
                        >
                          Устгах
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {/* Footer row with totals */}
                <tr className="bg-gray-100 font-bold">
                  <td className="px-3 py-2 border-b border-gray-200">
                    <span className="text-gray-700">Нийт:</span>
                  </td>
                  <td className="px-3 py-2 border-b border-gray-200">
                    <span className="text-gray-700">
                      {calculateFooterTotals().activeTotal.toLocaleString('mn-MN', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}
                    </span>
                  </td>
                  <td className="px-3 py-2 border-b border-gray-200">
                    <span className="text-gray-700">
                      {calculateFooterTotals().passiveTotal.toLocaleString('mn-MN', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}
                    </span>
                  </td>
                  <td className="px-3 py-2 border-b border-gray-200"></td>
                  <td className="px-3 py-2 border-b border-gray-200"></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Цуцлах
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Хадгалж байна...' : 'Хадгалах'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountPeriodBalanceItemPopup;

